/**
 * makeClickable — the single source of truth for "this thing is tappable".
 *
 * Two jobs, both load-bearing for a touch-first, mouse-friendly cozy UI:
 *
 *  1. POINTER CURSOR. On a desktop the mouse must turn into a hand over anything
 *     clickable. Phaser drives the canvas cursor from `gameObject.input.cursor`:
 *     on POINTER_OVER it runs `InputManager.setCursor(io)` which does
 *     `canvas.style.cursor = io.cursor` (and resets to the default on OUT). So the
 *     ONLY thing needed for the hand cursor is a truthy `input.cursor` — we set it
 *     to `'pointer'`. (`useHandCursor: true` is just sugar Phaser rewrites to the
 *     same `io.cursor = 'pointer'`; we set it directly so it survives the
 *     persistent-input-object path on resize too.)
 *
 *  2. A HIT AREA THAT MATCHES THE VISUAL. Our cards/rows/tiles are drawn CENTERED
 *     (origin 0.5, graphics spanning [-w/2..w/2, -h/2..h/2]) on the object's x/y, so
 *     the hit area must cover that same centered band. The tricky part is Phaser's
 *     hit-test space: before testing, InputManager does `point += displayOrigin`
 *     (InputManager.pointWithinHitArea), shifting the pointer from "centered on the
 *     object's transform" into the object's TOP-LEFT-anchored display space. So the
 *     correct hitArea anchor depends on the object's displayOrigin:
 *       - A Container with NO setSize (Button) has width=0 ⇒ displayOrigin = 0, so
 *         Phaser adds nothing and the point stays centered ⇒ a CENTERED rect
 *         (-w/2,-h/2,w,h) is right.
 *       - A Zone from `scene.add.zone(x,y,w,h)` (Menu rows) has origin 0.5 ⇒
 *         displayOrigin = w/2,h/2, so Phaser shifts the point into top-left space ⇒ a
 *         TOP-LEFT rect (0,0,w,h) is right. A centered rect there is offset by half a
 *         box (the "tap selects the wrong row" bug). The same is true for any
 *         Container that DID call setSize (Toast, DialogBox, collection tiles).
 *     One formula covers every case: anchor the rect at displayOrigin - (w/2,h/2).
 *     After Phaser adds displayOrigin, the rect is always centered on the transform,
 *     matching the centered visual for displayOrigin = 0 (Container, no setSize) AND
 *     displayOrigin = w/2 (Zone, or any sized object) alike.
 *
 * Reuses an existing hit rect on re-application (resize) so the clickable region
 * tracks the drawn bounds exactly — mirrors Button.applyHitArea().
 */
import Phaser from 'phaser';

export interface ClickableOptions {
  /** Pointer cursor on hover (desktop). Defaults to 'pointer' (the hand). */
  readonly cursor?: string;
  /** Tap/click handler, wired to POINTER_DOWN. Optional — omit to only set the area. */
  readonly onClick?: (pointer: Phaser.Input.Pointer) => void;
}

/**
 * Make `obj` interactive with a hit rect of exactly (w x h) — anchored so it ends up
 * centered on the object's transform once Phaser applies the displayOrigin shift (works
 * for both a width-0 Container and an origin-0.5 Zone) — plus a pointer cursor. Safe to
 * call repeatedly (resize): it mutates the existing hit rect in place rather than
 * stacking shapes. Returns `obj` for chaining.
 */
export function makeClickable<T extends Phaser.GameObjects.GameObject>(
  obj: T,
  width: number,
  height: number,
  options: ClickableOptions = {},
): T {
  const cursor = options.cursor ?? 'pointer';
  // Anchor the rect so that, AFTER Phaser adds the object's displayOrigin during the
  // hit-test, the rect lands centered on the transform (matching the centered visual).
  // displayOrigin is 0 for a Container with no setSize (Button) and w/2,h/2 for a Zone
  // or any sized object — this one expression is correct for both. See the file header.
  const origin = obj as unknown as { displayOriginX?: number; displayOriginY?: number };
  const x = (origin.displayOriginX ?? 0) - width / 2;
  const y = (origin.displayOriginY ?? 0) - height / 2;

  if (obj.input?.hitArea instanceof Phaser.Geom.Rectangle) {
    // The input object persists across disable/resize. Phaser's setInteractive only
    // re-enables an existing input (it won't re-apply a new shape), so mutate the
    // hit rect in place and re-enable explicitly.
    obj.input.hitArea.setTo(x, y, width, height);
    obj.input.enabled = true;
  } else {
    obj.setInteractive(
      new Phaser.Geom.Rectangle(x, y, width, height),
      Phaser.Geom.Rectangle.Contains,
    );
  }
  // The ONE thing that flips canvas.style.cursor to a hand on hover.
  if (obj.input) obj.input.cursor = cursor;

  if (options.onClick) {
    // Replace any prior handler from a previous call so resize re-application never
    // stacks duplicate listeners (the appended-array bug class, but for events).
    obj.off(Phaser.Input.Events.POINTER_DOWN, options.onClick);
    obj.on(Phaser.Input.Events.POINTER_DOWN, options.onClick);
  }
  return obj;
}

/**
 * Set ONLY the pointer cursor on an already-interactive object (it keeps its existing
 * hit area — for a non-rectangular shape like a circle, or a Zone created with
 * `.setInteractive({...})` where the area is already correct). Idempotent.
 */
export function setPointerCursor<T extends Phaser.GameObjects.GameObject>(
  obj: T,
  cursor = 'pointer',
): T {
  if (obj.input) obj.input.cursor = cursor;
  return obj;
}
